/* eslint-disable */
// Vendor
import React, { useRef, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { withRouter } from 'next/router';
import {
  promosAuthorSelector,
  metaAuthorSelector,
  infoAuthorSelector,
} from 'state/author/selectors';
import { loadArticles, loadAuthorIfNotLoaded } from 'state/author/actions';
import { createURL } from 'utils/url';

// Components
import Container from 'components/Container';
import Page from 'components/Page';

import { AuthorBlock, AuthorFilter, AuthorContent, AuthorPartners } from 'components/pages/Author';

// Custom Hooks
import useTheme from 'hooks/useTheme';
import usePromos from 'hooks/usePromos';

// Styles
import styles from './styles.styl';

const REQURED_PROMOS = {
  header: { 'big-image': 1 },
  content: { image: 2 },
  footer: { image: 2 },
};

function AuthorPage({ router }) {
  const dispatch = useDispatch();
  const author = router.query.slug;
  const meta = useSelector(state => metaAuthorSelector(state, author));
  const promo = useSelector(state => promosAuthorSelector(state, author));
  const authorData = useSelector(state => infoAuthorSelector(state, author));
  const sortOptions = useSelector(state => state.author.sortOptions);
  const categories = useSelector(state => state.home.categories);

  const authorRef = useRef();
  const title = meta['meta-title'] ? meta['meta-title'].content : authorData.name;
  const category = router.query.category || '';
  const sort = router.query.sort || sortOptions[0].value;

  const setTheme = useTheme().setTheme;
  const updatePromos = usePromos(promo, null, REQURED_PROMOS);

  // Формирование ссылки
  const getPageHref = useCallback(
    (author, page, sort, category) =>
      `/author/${author}?page=${page}&sort=${sort}&category=${category}`,
    [],
  );

  /** Адрес для ссылок в пагинации */
  const setPaginationHref = useCallback(page => getPageHref(author, page, sort, category), [
    author,
    sort,
    category,
    getPageHref,
  ]);

  /** Смена URL.
   * Данный метод устанавливает новые значения page и sort
   * не вызывая getInitialProps страницы
   *
   * @param {number} newPage
   * @param {string} newSort
   */
  const changeRoute = useCallback(
    (newPage, newSort, newCategory) => {
      router.push(
        createURL('/author', {
          slug: author,
          page: newPage,
          sort: newSort,
          category: newCategory,
          'no-scroll': true,
        }),
        getPageHref(author, newPage, newSort, newCategory),
        {
          shallow: true,
          ...router.query,
        },
      );
    },
    [author, getPageHref, router],
  );

  /** Смена секции.
   * Устанавливает новое значение section
   * @param {string} newSort
   */
  const changeSort = useCallback(
    newSort => {
      changeRoute(1, newSort, category);
      dispatch(loadArticles(author, 1, newSort, category));
    },
    [author, category, changeRoute, dispatch],
  );

  /** Смена категории.
   * Устанавливает новое значение категории
   * @param {string} newCategory
   */
  const changeCategory = useCallback(
    newCategory => {
      changeRoute(1, sort, newCategory);
      dispatch(loadArticles(author, 1, sort, newCategory));
    },
    [author, changeRoute, dispatch, sort],
  );

  /** Смена странницы.
   * Устанавливает новое значение section
   * @param {number} page
   */
  const changePage = useCallback(
    newPage => {
      changeRoute(newPage, sort, category);
      dispatch(loadArticles(author, newPage, sort, category));
      window.scroll({
        top: authorRef.current ? authorRef.current.offsetTop : 0,
        left: 0,
        behavior: 'smooth',
      });
    },
    [author, category, changeRoute, dispatch, sort],
  );

  // Смена темы
  useEffect(() => {
    setTheme({ theme: 'default', bgImage: null, bgColor: null });
  }, [setTheme]);

  return (
    <Page title={title} meta={meta} className={styles.AuthorPage}>
      <Container>
        <Page.Section className={styles.AuthorPage__header}>
          <AuthorBlock ref={authorRef} className={styles.AuthorPage__author} {...authorData} />
          <AuthorFilter
            className={styles.AuthorPage__filter}
            title={title}
            category={category}
            categories={categories}
            changeCategory={changeCategory}
            sort={sort}
            options={sortOptions}
            changeSort={changeSort}
          />
        </Page.Section>

        <AuthorContent
          promos={promo.content}
          slug={author}
          sort={sort}
          paginationHref={setPaginationHref}
          changePage={changePage}
        />
        <AuthorPartners slug={author} />
      </Container>
    </Page>
  );
}

AuthorPage.getInitialProps = async ({ store, query }) => {
  const { sortOptions } = store.getState().author;
  const page = Number(query.page) || 1;
  const sort = query.sort || sortOptions[0].value;
  const category = query.category || '';

  return store
    .dispatch(loadAuthorIfNotLoaded(encodeURIComponent(query.slug), page, sort, category))
    .then(() => ({}));
};

AuthorPage.propTypes = {
  author: PropTypes.string.isRequired,
  router: PropTypes.object.isRequired,
};

export default withRouter(AuthorPage);
